import { ChoiceFlat } from "@pdfking/forms"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import { ArrayQ } from "@pdfking/structs"
import { Length, RecordMap } from "@pdfking/tfunc"

import { IDMixin } from "../../mixins/IDMixin"
import { DjButton } from "../fields/DjButton"
import { DjDatalist } from "../fields/DjDatalist"
import { DjSelect } from "../fields/DjSelect"
import { FieldMixin } from "../mixins/FieldMixin"
import { ChoiceFormMixin } from "../mixins/FormMixin"
import { NodeMixin } from "../mixins/NodeMixin"
import type { AnyChoices, GroupedChoices } from "../mixins/SelectMixin"
import { dom_id } from "./DomId"
import { FULL_COL } from "./FieldLayout"
import { FieldShell } from "./FieldShell"
import { ViewMixin } from "./ViewMixin"

export const ChoiceField = CreateRenderFunction({
  mixins: [NodeMixin, FieldMixin, IDMixin, ViewMixin, ChoiceFormMixin] as const,
  render() {
    const node = this.node!
    const id = dom_id(this)
    // Every choice labelled; grouped ones keep their groups for the select
    const flat = RecordMap(node.format, ChoiceFlat(node.choices))
    const grouped: AnyChoices = ArrayQ(node.choices)
      ? node.choices.map(([title, group]): GroupedChoices[number] => [
          title,
          RecordMap(node.format, group),
        ])
      : flat
    const shared = {
      choices: flat,
      disabled: this.disabled,
      dom_id: id,
      input: node.input,
      onselect: node.validate,
    }
    const shell = {
      cell: this.cell,
      className: this.className,
      error: node.error,
      field_key: this.field_key,
      hide_labels: this.hide_labels,
      path: id,
    }
    if (node.multiple) {
      // A joined button group can't wrap without breaking the segment look,
      // so a multi-select takes its own full line.
      return (
        <FieldShell {...shell} col={FULL_COL}>
          <DjButton {...shared} multiple={node.multiple} />
        </FieldShell>
      )
    }
    // Few choices read best as a button group, many need type-ahead, the
    // middle ground stays a plain select.
    const count = Length(flat)
    return (
      <FieldShell {...shell}>
        {count > 0 && count < 4 ? (
          <DjButton {...shared} />
        ) : count > 99 ? (
          <DjDatalist {...shared} />
        ) : (
          <DjSelect {...shared} choices={grouped} error={node.error} />
        )}
      </FieldShell>
    )
  },
})
